import { Divider, Tab, Tabs, Typography } from '@material-ui/core';
import { FormattedDate } from '@superdispatch/dates';
import { useValueObserver } from '@superdispatch/hooks';
import {
  Column,
  Columns,
  InfoCard,
  Inline,
  Stack,
  Tag,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { chooseUTMSource } from 'shared/helpers/Analytics';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { ShipperAvatar } from 'shared/modules/shipper/ShipperAvatar';
import { ShipperProfileDTO } from 'shared/modules/shipper/ShipperProfileDTO';
import { ShipperProfilePageParams } from 'shared/modules/shipper/ShipperProfilePageParams';
import { ResponsiveTooltip } from 'shared/ui/ResponsiveTooltip';
import styled from 'styled-components';
import { displayShipperVehiclesCount } from '../../data/dto/PostingLoadDTO';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { ReactNode } from 'react';

const FullWidthDivider = styled(Divider)`
  position: relative;
  left: -16px;
  width: calc(100% + 32px);

  ${({ theme }) => theme.breakpoints.up('sm')} {
    left: -24px;
    width: calc(100% + 48px);
  }
`;

const VerticalDividerContainer = styled.div`
  height: 16px;
`;

const VerticalDivider = () => (
  <VerticalDividerContainer>
    <Divider orientation="vertical" />
  </VerticalDividerContainer>
);

interface Props {
  activeTab: ShipperProfilePageParams['tab'];
  ratingSection: ReactNode;
  profile: ShipperProfileDTO;
}

export function ShipperProfileHeader({
  activeTab,
  ratingSection,
  profile,
}: Props) {
  const [_query, setQuery] = useQuery();
  const shouldShowDivider = useResponsiveValue(false, false, true);

  const showInsightsSection =
    profile.accepted_loads_count ??
    profile.overall_moved_vehicles_count ??
    profile.six_month_moved_vehicles_count;

  useValueObserver(activeTab, (prevActiveTab) => {
    if (prevActiveTab !== 'ratings' && activeTab === 'ratings') {
      trackLoadboardEvent({ name: 'SLB: Opened Shipper’s Ratings' });
    }
  });

  return (
    <InfoCard size="large">
      <Stack space="small">
        <Columns
          space="small"
          align={['top', 'center', 'center']}
          collapseBelow="tablet"
        >
          <Column width="content">
            <ShipperAvatar
              name={profile.name}
              logo={profile.logo_url}
              size="large"
            />
          </Column>
          <Column>
            <Stack space="xxsmall">
              <Inline
                space={['xxsmall', 'xxsmall', 'xsmall']}
                verticalAlign="center"
              >
                <Typography variant="h1" aria-label="shipper name">
                  {profile.name}
                </Typography>

                {profile.verification_status === 'NOT_APPROVED' && (
                  <ResponsiveTooltip title="This shipper hasn't verified all documents yet.">
                    <Tag
                      aria-label="unverified shipper"
                      color="grey"
                      variant="subtle"
                    >
                      Unverified
                    </Tag>
                  </ResponsiveTooltip>
                )}
              </Inline>

              {profile.last_offer_accepted_date && (
                <Typography color="textSecondary">
                  <FormattedDate
                    variant="Date"
                    format="DateISO"
                    date={profile.last_offer_accepted_date}
                  />{' '}
                  last worked with you
                </Typography>
              )}
            </Stack>
          </Column>

          <Column width="content">{ratingSection}</Column>
        </Columns>

        {showInsightsSection != null && (
          <>
            <FullWidthDivider />

            <Columns
              align={['top', 'top', 'center']}
              space="small"
              collapseBelow="desktop"
            >
              {profile.accepted_loads_count != null && (
                <>
                  <Column width="content">
                    <Inline
                      aria-label="accepted loads"
                      space="xsmall"
                      verticalAlign="center"
                    >
                      <Typography variant="h2">
                        {profile.accepted_loads_count}
                      </Typography>

                      <Typography variant="h4" color="textSecondary">
                        {formatPlural(
                          profile.accepted_loads_count,
                          'load',
                          'loads',
                        )}{' '}
                        accepted by you
                      </Typography>
                    </Inline>
                  </Column>

                  {shouldShowDivider &&
                    profile.overall_moved_vehicles_count != null && (
                      <Column width="content">
                        <VerticalDivider />
                      </Column>
                    )}
                </>
              )}

              {profile.overall_moved_vehicles_count != null && (
                <>
                  <Column width="content">
                    <Inline
                      aria-label="overall moved vehicles"
                      space="xsmall"
                      verticalAlign="center"
                    >
                      <Typography variant="h2">
                        {displayShipperVehiclesCount(
                          profile.overall_moved_vehicles_count,
                        )}
                      </Typography>

                      <Typography variant="h4" color="textSecondary">
                        vehicles moved overall
                      </Typography>
                    </Inline>
                  </Column>

                  {shouldShowDivider &&
                    profile.six_month_moved_vehicles_count != null && (
                      <Column width="content">
                        <VerticalDivider />
                      </Column>
                    )}
                </>
              )}

              {profile.six_month_moved_vehicles_count != null && (
                <Column width="content">
                  <Inline
                    aria-label="vehicles moved in 6 months"
                    space="xsmall"
                    verticalAlign="center"
                  >
                    <Typography variant="h2">
                      {displayShipperVehiclesCount(
                        profile.six_month_moved_vehicles_count,
                      )}
                    </Typography>

                    <Typography variant="h4" color="textSecondary">
                      vehicles moved in 6 months
                    </Typography>
                  </Inline>
                </Column>
              )}
            </Columns>
          </>
        )}

        <Box margin={['-small', '-medium']} marginTop="none">
          <Tabs
            value={activeTab}
            onChange={(_, value: 'overview' | 'ratings') => {
              setQuery(
                {
                  tab: value,
                  utm_source: chooseUTMSource(),
                  utm_medium: 'Shipper Profile',
                  utm_content:
                    value === 'overview' ? 'Overview Tab' : 'Ratings Tab',
                },
                true,
              );
            }}
          >
            <Tab value="overview" label="Overview" />
            <Tab value="ratings" label="Ratings" />
          </Tabs>
        </Box>
      </Stack>
    </InfoCard>
  );
}
